import { useState, useEffect, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Loader2,
  AlertCircle,
  Briefcase,
  Mail,
  Clock,
  ExternalLink,
  Phone,
  Download,
  Award,
} from "lucide-react"
import logo from "../assets/logo_h.svg"
import linkedin from "../assets/icons/linkedin.png"
import github from "../assets/icons/github.png"
import leetcode from "../assets/icons/leetcode.png"
import internet from "../assets/icons/internet.png"
import placeholder from "../assets/placeholder.png"
import ContactPanel from "../components/ContactPanel"
import { sendEmail } from "../services/emailService"

const SmartProfilePage = () => {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isContactPanelOpen, setIsContactPanelOpen] = useState(false)
  const [showAllSkills, setShowAllSkills] = useState(false)
  const hasTrackedRef = useRef(false)

  useEffect(() => {
    // Early return if no userId
    if (!userId) {
      setError("No user ID provided")
      setLoading(false)
      return
    }

    let isMounted = true

    const trackProfileVisit = async (userId, locationData) => {
      try {
        await fetch(
          "https://hire100x-e1dd4.uc.r.appspot.com/users/track-profile-visit",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId,
              location: locationData,
            }),
          },
        )
      } catch (err) {
        console.error("Failed to track profile visit:", err)
      }
    }

    const sendResumeViewNotification = async (userEmail, locationData) => {
      const now = new Date()
      const viewTime = now.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      const viewDate = now.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      })

      // Format location string
      const location =
        locationData?.city && locationData?.region
          ? `${locationData.city}, ${locationData.region}`
          : locationData?.city
            ? locationData.city
            : "Unknown location"

      try {
        await sendEmail({
          email: userEmail,
          subject: "Someone viewed your resume",
          text: `Someone viewed your resume at ${viewTime} on ${viewDate} from ${location}`,
          type: "resolut-profile-visit",
          html: `
        <div style="font-family: -apple-system, BlinkMacSystemFont, Arial, sans-serif; max-width: 480px; margin: 0 auto; padding: 32px 20px;">
          
          <h1 style="color: #111827; font-size: 22px; font-weight: 600; margin: 0 0 16px 0;">
            Someone viewed your resume 👀
          </h1>
          
          <p style="color: #4b5563; font-size: 16px; line-height: 1.5; margin: 0 0 8px 0;">
            <strong>${viewTime}</strong> • ${viewDate} from ${location}
          </p>
          
          <p style="color: #9ca3af; font-size: 14px; margin: 24px 0 0 0; padding-top: 24px; border-top: 1px solid #e5e7eb;">
            Resolut
          </p>
        </div>
      `,
        })
      } catch (err) {
        console.error("Failed to send resume view notification:", err)
      }
    }

    const fetchLocation = async () => {
      try {
        const geoResponse = await fetch("https://api.ipbase.com/v1/json/")
        if (geoResponse.ok) {
          const geoData = await geoResponse.json()
          return {
            city: geoData.city || null,
            country: geoData.country_name || null,
            region: geoData.region_name || null,
          }
        }
      } catch (geoError) {
        console.error("Failed to fetch location data:", geoError)
      }
      return { city: null, country: null, region: null }
    }

    const fetchUserProfile = async () => {
      try {
        setLoading(true)
        setError(null)

        const response = await fetch(
          `https://hire100x-e1dd4.uc.r.appspot.com/users/${userId}`,
        )

        if (!response.ok) {
          throw new Error(
            response.status === 404
              ? "User profile not found"
              : `Failed to fetch profile: ${response.statusText}`,
          )
        }

        const data = await response.json()

        if (!data.success || !data.data) {
          throw new Error("Invalid response format")
        }

        if (isMounted) {
          setUser(data.data)
        }

        // Only track and send email if not already tracked
        if (!hasTrackedRef.current) {
          hasTrackedRef.current = true

          // Fetch location once
          const locationData = await fetchLocation()

          // Fire and forget - parallel execution with location data
          const asyncTasks = [trackProfileVisit(userId, locationData)]

          if (data.data.email) {
            asyncTasks.push(
              sendResumeViewNotification(data.data.email, locationData),
            )
          }

          Promise.allSettled(asyncTasks)
        }
      } catch (err) {
        console.error("Error fetching user profile:", err)
        if (isMounted) {
          setError(err.message || "Failed to load profile")
        }
      } finally {
        if (isMounted) {
          setLoading(false)
        }
      }
    }

    fetchUserProfile()

    return () => {
      isMounted = false
    }
  }, [userId])

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading profile...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-md w-full border border-gray-200 p-8 text-center">
          <AlertCircle className="w-10 h-10 text-gray-400 mx-auto mb-3" />
          <h2 className="text-lg font-medium text-gray-900 mb-2">
            Profile Not Found
          </h2>
          <p className="text-sm text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate("/")}
            className="px-5 py-2 bg-gray-900 text-white text-sm hover:bg-gray-800 transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');
        
        * {
          font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
        }
        
        @media print {
          .no-print { display: none !important; }
          body { background: white; }
        }
      `}</style>

      {/* Simple Header */}
      <header className="border-b border-gray-100 no-print">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={logo} alt="Logo" className="h-6" />
            <span className="text-xl font-bold text-[#000000de]">Resolut</span>
          </div>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900 transition-colors"
          >
            <Download size={14} />
            Download PDF
          </button>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Two Column Layout - 70/30 */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-8">
          {/* Left Column - Main Content (70%) */}
          <div className="lg:col-span-7 space-y-8">
            {/* Header Section */}
            <div className="pb-8 border-b border-gray-100">
              <div className="flex flex-col gap-4">
                {/* Top Row: Profile Picture + Info */}
                <div className="flex items-start gap-3 sm:gap-6">
                  {/* Profile Image - Always on left */}
                  <img
                    src={user?.profileImageUrl || placeholder}
                    alt={user?.name || "User"}
                    className="w-16 h-16 sm:w-24 sm:h-24 rounded-full object-cover flex-shrink-0"
                  />

                  {/* Info - Aligned to right of profile */}
                  <div className="flex-1 min-w-0">
                    <h1 className="text-lg sm:text-2xl font-medium text-gray-900 mb-1">
                      {user?.name || "User Name"}
                    </h1>

                    {/* Job Title & Company */}
                    <p className="text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                      {user?.personal_information?.currentJobTitle ||
                        "Senior Software Developer"}
                      {user?.personal_information?.currentJobTitle &&
                        user?.personal_information?.currentCompany && (
                          <span className="text-gray-400 mx-1.5">at</span>
                        )}
                      {user?.personal_information?.currentCompany && (
                        <span className="text-gray-900">
                          {user.personal_information.currentCompany}
                        </span>
                      )}
                    </p>

                    {/* Badges */}
                    <div className="flex flex-wrap items-center gap-2 mb-2 sm:mb-3">
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-gray-100/60 rounded-md">
                        <Briefcase size={13} className="text-gray-500" />
                        <span className="text-xs text-gray-600">
                          {user?.personal_information?.yearsExperience ||
                            "5"}{" "}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 px-2.5 py-1 bg-blue-50/80 rounded-md">
                        <Clock size={13} className="text-blue-600" />
                        <span className="text-xs text-blue-700">
                          {user?.job_preferences?.noticePeriod || "30 Days"}{" "}
                          notice
                        </span>
                      </div>
                    </div>

                    {/* Contact Row with Phone */}
                    {user?.phone && (
                      <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600 mb-2 sm:mb-3">
                        <Phone size={14} className="text-gray-400" />
                        <span>{user.phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Bottom Row: Social Links + Action Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
                  {/* Social Links */}
                  <div className="flex items-center gap-3 justify-center sm:justify-start">
                    {[
                      {
                        icon: linkedin,
                        alt: "LinkedIn",
                        link: user?.social_links?.linkedin,
                      },
                      {
                        icon: github,
                        alt: "GitHub",
                        link: user?.social_links?.github,
                      },
                      {
                        icon: leetcode,
                        alt: "LeetCode",
                        link: user?.social_links?.leetcode,
                      },
                      {
                        icon: internet,
                        alt: "Portfolio",
                        link: user?.social_links?.portfolio,
                      },
                    ].map((social, index) => (
                      <button
                        key={index}
                        onClick={() =>
                          social.link && window.open(social.link, "_blank")
                        }
                        className="p-2 rounded-lg transition-all duration-200 bg-gray-50 hover:bg-gray-100 hover:shadow-sm border border-gray-200/50 cursor-pointer"
                        title={social.alt}
                        disabled={!social.link}
                      >
                        <img
                          src={social.icon}
                          alt={social.alt}
                          className="h-4 w-4"
                        />
                      </button>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-3">
                    {/* Resume Download Button */}
                    <button
                      onClick={() => {
                        if (user?.resumeUrl) {
                          const link = document.createElement("a")
                          link.href = user.resumeUrl
                          link.download = `${user?.name || "resume"}_resume.pdf`
                          link.target = "_blank"
                          document.body.appendChild(link)
                          link.click()
                          document.body.removeChild(link)
                        }
                      }}
                      className="flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-white text-gray-700 text-sm font-medium hover:bg-gray-50 transition-colors rounded-full border border-gray-300 flex-1 sm:flex-initial"
                    >
                      <Download size={16} />
                      Resume
                    </button>

                    {/* Contact Button */}
                    <button
                      onClick={() => setIsContactPanelOpen(true)}
                      className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 bg-gray-900 text-white text-sm font-medium hover:bg-gray-800 transition-colors rounded-full flex-1 sm:flex-initial"
                    >
                      <Mail size={18} />
                      Contact
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Work Experience */}
            {user?.work_history && user.work_history.length > 0 && (
              <div>
                <h2 className="text-[17px] font-medium text-gray-900 mb-4">
                  Work Experience
                </h2>

                <div className="space-y-6">
                  {user.work_history.map((work, index) => (
                    <div key={index} className="flex items-start gap-3">
                      {/* Company Logo */}
                      <img
                        src={placeholder}
                        alt={work.companyName || "Company"}
                        className="w-10 h-10 rounded object-cover flex-shrink-0"
                      />

                      <div className="flex-1">
                        <div className="flex items-baseline justify-between mb-1">
                          <div>
                            <h3 className="text-[15px] font-medium text-gray-900">
                              {work.role || "Front-End Developer"}
                            </h3>
                            <p className="text-sm text-gray-600">
                              {work.companyName || "Trendyol.com"}
                            </p>
                          </div>
                          <p className="text-xs text-gray-500 flex-shrink-0 ml-4">
                            {work.startMonth && work.startYear
                              ? `${work.startMonth} ${work.startYear} - ${
                                  work.isCurrentJob ||
                                  (!work.endMonth && !work.endYear)
                                    ? "Present"
                                    : `${work.endMonth} ${work.endYear}`
                                }`
                              : "Present"}
                          </p>
                        </div>
                        {work.description && (
                          <p className="text-[15px] text-gray-700 leading-relaxed whitespace-pre-line mt-2 font-sans">
                            {work.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Education */}
            {user?.education && user.education.length > 0 && (
              <div>
                <h2 className="text-[17px] font-medium text-gray-900 mb-4">
                  Education
                </h2>

                <div className="space-y-4">
                  {user.education.map((entry, index) => (
                    <div key={index} className="flex items-start gap-3">
                      {/* School Logo */}
                      <img
                        src={placeholder}
                        alt={entry.institute || "School"}
                        className="w-10 h-10 rounded object-cover flex-shrink-0"
                      />

                      <div className="flex-1">
                        <div className="flex items-baseline justify-between mb-0.5">
                          <h3 className="text-[15px] font-medium text-gray-900">
                            {entry.degree ||
                              "Master degree in Computer science and Mathematics"}
                            {entry.field && (
                              <span className="text-gray-500 font-normal">
                                {", "}
                                {entry.field}
                              </span>
                            )}
                          </h3>
                          <p className="text-xs text-gray-500 flex-shrink-0 ml-4">
                            {entry.startMonth &&
                            entry.startYear &&
                            entry.endMonth &&
                            entry.endYear
                              ? `${entry.startMonth} ${entry.startYear} - ${entry.endMonth} ${entry.endYear}`
                              : "2017"}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600">
                          {entry.institute || "Middle Earth Technic University"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Projects */}
            {user?.projects && user.projects.length > 0 && (
              <div>
                <h2 className="text-[17px] font-medium text-gray-900 mb-4">
                  Projects
                </h2>

                <div className="space-y-4">
                  {user.projects.map((project, index) => (
                    <div key={index}>
                      <div className="flex items-start justify-between mb-1">
                        <h3 className="text-[16px] font-medium text-gray-700 font-semibold">
                          {project.title || "Project Title"}
                        </h3>
                        {project.url && (
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gray-400 hover:text-gray-900 transition-colors flex-shrink-0"
                          >
                            <ExternalLink size={16} />
                          </a>
                        )}
                      </div>
                      <p className="text-[15px] text-gray-700 leading-relaxed mb-1 font-sans">
                        {project.description || "Project description"}
                      </p>
                      {project.url && (
                        <a
                          href={project.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[15px] text-blue-600"
                        >
                          {project.url}
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Sidebar (30%) */}
          <div className="lg:col-span-3 space-y-6">
            {/* Skills */}
            <div>
              <h3 className="text-[17px] font-medium text-gray-900 mb-4">
                Skills
              </h3>

              <div className="flex flex-wrap gap-2">
                {user?.resumeData?.skills?.length > 0 ? (
                  <>
                    {(showAllSkills
                      ? user.resumeData.skills
                      : user.resumeData.skills.slice(0, 15)
                    ).map((skill, index) => (
                      <span
                        key={index}
                        className="px-3 py-1.5 bg-gray-50 text-gray-700 text-[14px] rounded-2xl font-sans hover:bg-gray-100 transition-all"
                      >
                        {skill}
                      </span>
                    ))}
                    {user.resumeData.skills.length > 15 && (
                      <button
                        onClick={() => setShowAllSkills(!showAllSkills)}
                        className="px-3 py-1.5 text-[14px] rounded-2xl font-sans font-medium text-blue-600 hover:bg-blue-50 transition-all cursor-pointer"
                      >
                        {showAllSkills
                          ? "Show less"
                          : `+${user.resumeData.skills.length - 15} more`}
                      </button>
                    )}
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">No skills added yet</p>
                )}
              </div>
            </div>
            {/* Achievements */}
            {user?.achievements && user.achievements.length > 0 && (
              <div>
                <h3 className="text-[17px] font-medium text-gray-900 mb-4">
                  Achievements
                </h3>

                <div className="space-y-3">
                  {user.achievements.map((achievement, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <div className="flex-shrink-0 mt-0.5">
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center">
                          <Award size={14} className="text-white" />
                        </div>
                      </div>
                      <div className="flex-1">
                        {achievement.title && (
                          <p className="text-[15px] font-medium text-gray-900 mb-0.5">
                            {achievement.title}
                          </p>
                        )}
                        <p
                          className={`text-[15px] ${
                            achievement.title
                              ? "text-gray-600"
                              : "text-gray-900"
                          }`}
                        >
                          {achievement.achievement}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="pt-6 mt-8 border-t border-gray-100 text-center">
          <p className="text-xs text-gray-400">
            Generated by Resolut · Professional Profile
          </p>
        </div>
      </div>

      <ContactPanel
        isOpen={isContactPanelOpen}
        onClose={() => setIsContactPanelOpen(false)}
        userEmail={user?.email}
        userName={user?.name}
      />
    </div>
  )
}

export default SmartProfilePage
